import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, HelpCircle, Send, Loader2, Mail } from 'lucide-react';
import { API_URL } from '../utils/api';

const CATEGORIES = [
  { id: 'bug',      label: 'Hiba / Bug' },
  { id: 'feature',  label: 'Fejlesztési ötlet' },
  { id: 'billing',  label: 'Számlázás / Előfizetés' },
  { id: 'account',  label: 'Fiókkal kapcsolatos' },
  { id: 'egyéb',    label: 'Egyéb' },
];

export default function SupportModal({ open, onClose }) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('egyéb');
  const [sending, setSending] = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (subject.trim().length < 3) { toast.error('Adj meg egy rövid tárgyat (min. 3 karakter).'); return; }
    if (message.trim().length < 10) { toast.error('Az üzenet túl rövid (min. 10 karakter).'); return; }
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/support/ticket`,
        { subject: subject.trim(), message: message.trim(), category },
        { withCredentials: true }
      );
      toast.success(data.message || 'Megkeresés elküldve');
      setSubject(''); setMessage(''); setCategory('egyéb');
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Küldési hiba');
    }
    setSending(false);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
      data-testid="support-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rounded-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col"
        style={{ backgroundColor: 'var(--background-surface)', boxShadow: 'var(--shadow-5)' }}
      >
        {/* Header */}
        <div
          className="px-6 py-5 flex items-start gap-3 flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, var(--accent-copper), var(--accent-copper-hover))', color: 'var(--accent-copper-fg)' }}
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(255,255,255,0.18)' }}>
            <HelpCircle size={20} strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-heading text-lg font-bold">Ügyfélszolgálat</h2>
            <p className="text-xs opacity-90 mt-0.5 flex items-center gap-1.5">
              <Mail size={11} /> support@acountix.eu
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="support-close"
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'var(--accent-copper-fg)' }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: 'var(--text-muted)' }}>
              Kategória
            </label>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map(c => (
                <button
                  key={c.id}
                  onClick={() => setCategory(c.id)}
                  data-testid={`support-cat-${c.id}`}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full transition-all"
                  style={category === c.id
                    ? { backgroundColor: 'var(--accent-copper)', color: 'var(--accent-copper-fg)' }
                    : { backgroundColor: 'var(--background-base)', color: 'var(--text-secondary)', border: '1px solid var(--border)' }}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: 'var(--text-muted)' }}>
              Tárgy *
            </label>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="support-subject"
              maxLength={200}
              placeholder="Röviden: miről szól az üzeneted?"
              className="w-full px-3 py-2.5 rounded-lg text-sm"
              style={{ border: '1px solid var(--border)', backgroundColor: 'var(--background-surface)', color: 'var(--text-primary)' }}
            />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: 'var(--text-muted)' }}>
              Üzenet *
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="support-message"
              rows={7}
              maxLength={10000}
              placeholder="Írd le részletesen, miben segíthetünk. Ha hibáról van szó, oszd meg az oldal nevét és a lépéseket."
              className="w-full px-3 py-2.5 rounded-lg text-sm resize-none"
              style={{ border: '1px solid var(--border)', backgroundColor: 'var(--background-surface)', color: 'var(--text-primary)' }}
            />
            <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>
              {message.length} / 10000 karakter
            </p>
          </div>

          <div className="rounded-lg p-3 text-xs flex gap-2" style={{ backgroundColor: 'var(--accent-copper-soft)', color: 'var(--text-secondary)' }}>
            <span style={{ color: 'var(--accent-copper)' }}>💬</span>
            <span>Válaszunkat a fiókodhoz tartozó email címre küldjük. Visszaigazoló emailt is kapsz a küldés után.</span>
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex justify-end gap-2 px-6 py-4 flex-shrink-0"
          style={{ borderTop: '1px solid var(--border)', backgroundColor: 'var(--background-base)' }}
        >
          <button
            onClick={onClose}
            disabled={sending}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            style={{ border: '1px solid var(--border)', color: 'var(--text-secondary)', backgroundColor: 'var(--background-surface)' }}
          >
            Mégse
          </button>
          <button
            onClick={submit}
            disabled={sending || !subject.trim() || !message.trim()}
            data-testid="support-send"
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-50"
            style={{ backgroundColor: 'var(--accent-copper)', color: 'var(--accent-copper-fg)' }}
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? 'Küldés…' : 'Küldés'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
